import type { AutomationId } from "@repo/app-types/entities/automation";
import type {
  AutomationRunId,
  AutomationRunStatus,
} from "@repo/app-types/entities/automationRun";
import { automationRunStreamEventSchema } from "@repo/app-types/entities/automationRunStream";
import {
  uiMessageChunkSchema,
  type ChatTransport,
  type UIMessage,
  type UIMessageChunk,
} from "ai";
import { createParser } from "eventsource-parser";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import {
  apiBaseUrl,
  getApiAuthorizationHeaderValue,
} from "../../api/shared.js";

/**
 * True once the mount has settled — one effect tick after mounting. Gating
 * stream work on this keeps StrictMode's discarded dev mount from opening a
 * connection (or consuming a demultiplexed stream) that outlives it.
 */
export function useIsMountSettled(): boolean {
  const [isMountSettled, setIsMountSettled] = useState(false);
  useEffect(() => {
    setIsMountSettled(true);
  }, []);
  return isMountSettled;
}

interface Deferred<T> {
  promise: Promise<T>;
  resolve: (value: T) => void;
  isSettled: boolean;
}

function createDeferred<T>(): Deferred<T> {
  let resolvePromise: (value: T) => void = () => undefined;
  const promise = new Promise<T>((resolve) => {
    resolvePromise = resolve;
  });
  const deferred: Deferred<T> = {
    promise,
    isSettled: false,
    resolve: (value) => {
      deferred.isSettled = true;
      resolvePromise(value);
    },
  };
  return deferred;
}

/**
 * Validates one wire value as an AI SDK v6 UI message chunk (the shape the
 * worker publishes), so the demultiplexed streams are typed without casts.
 */
async function parseUIMessageChunk(
  value: unknown,
): Promise<UIMessageChunk | null> {
  const validate = uiMessageChunkSchema().validate;
  if (!validate) {
    return null;
  }
  const validation = await validate(value);
  if (!validation.success) {
    console.warn("Dropping malformed automation run stream chunk", value);
    return null;
  }
  return validation.value;
}

/** One thread's demultiplexed chunk history plus its live subscribers. */
interface ThreadChannel {
  history: UIMessageChunk[];
  subscribers: Set<ReadableStreamDefaultController<UIMessageChunk>>;
  isEnded: boolean;
}

/** How long to wait before the first reconnect attempt after a drop. */
const INITIAL_RECONNECT_DELAY_MS = 1_000;
/** Backoff cap while the endpoint stays unreachable. */
const MAX_RECONNECT_DELAY_MS = 15_000;

/** Resolves after `ms`, or as soon as the signal aborts. */
function waitBeforeReconnect(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", finish);
      resolve();
    };
    const timer = setTimeout(finish, ms);
    signal.addEventListener("abort", finish);
  });
}

interface RunStreamConnection {
  /**
   * A UI-chunk stream for one thread: replays the chunks received so far, then
   * tails live ones until the thread (or the whole run stream) ends. Resolves
   * null when the run has no live stream (the endpoint answered 204). Every
   * call returns an independent stream, so each consumer replays from the
   * start.
   */
  connectToThread(
    threadId: string,
  ): Promise<ReadableStream<UIMessageChunk> | null>;
  /** Closes the connection and settles every thread stream. */
  abort(): void;
}

/**
 * Opens the run's multiplexed SSE endpoint and demultiplexes its events —
 * `{thread_id, chunk}` envelopes (see `automationRunStreamEventSchema`) — into
 * per-thread UI chunk streams. One browser connection serves the run's status,
 * the orchestrator, and every subagent, however many delegations the run fans
 * out to.
 *
 * The connection recovers on its own: a dropped stream (network hiccup, the
 * relay's duration cap, an API restart) reconnects with backoff until the run
 * answers 204 (terminal) or the client aborts. Each new connection replays the
 * run's whole backlog, so recovery resets every channel and calls
 * `onInvalidate` — consumers must discard what they rendered and resubscribe.
 *
 * `onRunStatus` fires for each `run_status` event; being last-write-wins it
 * needs no reset, so a backlog replay simply re-reports the latest status.
 */
function createRunStreamConnection(
  url: string,
  onInvalidate: () => void,
  onRunStatus: (status: AutomationRunStatus) => void,
): RunStreamConnection {
  const abortController = new AbortController();
  const channels = new Map<string, ThreadChannel>();
  const hasLiveStream = createDeferred<boolean>();
  let isStreamOver = false;

  function resolveChannel(threadId: string): ThreadChannel {
    let channel = channels.get(threadId);
    if (!channel) {
      channel = { history: [], subscribers: new Set(), isEnded: false };
      channels.set(threadId, channel);
    }
    return channel;
  }

  function dispatchChunk(threadId: string, chunk: UIMessageChunk): void {
    const channel = resolveChannel(threadId);
    if (channel.isEnded) {
      return;
    }
    channel.history.push(chunk);
    for (const subscriber of channel.subscribers) {
      subscriber.enqueue(chunk);
    }
  }

  function closeSubscribers(channel: ThreadChannel): void {
    for (const subscriber of channel.subscribers) {
      subscriber.close();
    }
    channel.subscribers.clear();
  }

  function endThread(threadId: string): void {
    const channel = resolveChannel(threadId);
    if (channel.isEnded) {
      return;
    }
    channel.isEnded = true;
    closeSubscribers(channel);
  }

  function endAllThreads(): void {
    isStreamOver = true;
    for (const threadId of channels.keys()) {
      endThread(threadId);
    }
  }

  /**
   * Restarts a channel from scratch: current subscribers are closed (their
   * consumers hold chunks that are about to be re-delivered) and the history
   * dropped, so post-`onInvalidate` subscribers replay only what follows.
   */
  function resetChannel(channel: ThreadChannel): void {
    closeSubscribers(channel);
    channel.history = [];
    channel.isEnded = false;
  }

  async function handleEventData(rawData: string): Promise<void> {
    let parsedJson: unknown;
    try {
      parsedJson = JSON.parse(rawData);
    } catch {
      return;
    }
    const event = automationRunStreamEventSchema.safeParse(parsedJson);
    if (!event.success) {
      return;
    }
    switch (event.data.type) {
      case "chunk": {
        const chunk = await parseUIMessageChunk(event.data.chunk);
        if (chunk) {
          dispatchChunk(event.data.thread_id, chunk);
        }
        return;
      }
      case "thread_end":
        endThread(event.data.thread_id);
        return;
      case "thread_reset": {
        // A re-run thread starts over — including after its previous attempt
        // ended — so consumers replay only the re-run. `onInvalidate` makes
        // already-connected ones (holding the prior attempt's chunks) restart
        // too, instead of appending the re-run onto them.
        resetChannel(resolveChannel(event.data.thread_id));
        onInvalidate();
        return;
      }
      case "run_status":
        onRunStatus(event.data.status);
        return;
    }
  }

  async function consumeEventStream(
    body: ReadableStream<Uint8Array>,
  ): Promise<void> {
    // The parser's callback is synchronous, so buffer each feed's events and
    // dispatch them (async) between reads.
    const pendingEvents: string[] = [];
    const parser = createParser({
      onEvent: (event) => {
        pendingEvents.push(event.data);
      },
    });
    const reader = body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      parser.feed(decoder.decode(value, { stream: true }));
      for (const data of pendingEvents) {
        await handleEventData(data);
      }
      pendingEvents.length = 0;
    }
  }

  async function run(): Promise<void> {
    let isFirstConnection = true;
    let reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
    try {
      while (!abortController.signal.aborted) {
        try {
          const response = await fetch(url, {
            headers: {
              authorization: await getApiAuthorizationHeaderValue(),
              "x-alpharun-commit-sha": __COMMIT_SHA__,
            },
            signal: abortController.signal,
          });
          if (response.status === 204) {
            // The run is (now) terminal — there is no live stream to tail.
            return;
          }
          if (!response.ok || !response.body) {
            // A definitive non-stream answer (auth, missing run) — don't
            // retry our way past it.
            return;
          }
          if (!isFirstConnection) {
            // The new connection replays the run's whole backlog; drop the
            // accumulated copy — and every consumer's, via `onInvalidate` —
            // so nothing is delivered twice.
            for (const channel of channels.values()) {
              resetChannel(channel);
            }
            onInvalidate();
          }
          isFirstConnection = false;
          reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
          hasLiveStream.resolve(true);
          await consumeEventStream(response.body);
          // The server closed the stream: the run's terminal sentinel, or the
          // relay's duration cap. Reconnecting tells them apart — a terminal
          // run answers 204 above.
        } catch (error) {
          if (!abortController.signal.aborted) {
            console.error("Automation run event stream failed", error);
          }
        }
        if (abortController.signal.aborted) {
          return;
        }
        await waitBeforeReconnect(reconnectDelayMs, abortController.signal);
        reconnectDelayMs = Math.min(
          reconnectDelayMs * 2,
          MAX_RECONNECT_DELAY_MS,
        );
      }
    } finally {
      hasLiveStream.resolve(false);
      endAllThreads();
    }
  }

  void run();

  return {
    connectToThread: async (threadId) => {
      if (!(await hasLiveStream.promise)) {
        return null;
      }
      const channel = resolveChannel(threadId);
      let subscribed: ReadableStreamDefaultController<UIMessageChunk> | null =
        null;
      return new ReadableStream<UIMessageChunk>({
        start(controller) {
          for (const chunk of channel.history) {
            controller.enqueue(chunk);
          }
          if (channel.isEnded || isStreamOver) {
            controller.close();
            return;
          }
          subscribed = controller;
          channel.subscribers.add(controller);
        },
        cancel() {
          if (subscribed) {
            channel.subscribers.delete(subscribed);
          }
        },
      });
    },
    abort: () => abortController.abort(),
  };
}

interface AutomationRunStreamContextValue {
  /**
   * Read-only chat transport that resolves each `useChat` id (built by
   * `useAutomationRunStreamChatId` from an agent thread id) against the run's
   * multiplexed connection. Stable for the provider's lifetime — `useChat`
   * captures its transport at creation.
   */
  transport: ChatTransport<UIMessage>;
  /**
   * Bumped whenever consumers' accumulated chunks went stale — a reconnect's
   * backlog replay or a `thread_reset`. Baked into every consumer's chat id
   * (see `useAutomationRunStreamChatId`), so the bump resets each `useChat`
   * and re-resumes it against the replayed stream.
   */
  chatGeneration: number;
  /**
   * The run's latest streamed lifecycle status, or undefined before any
   * arrives (callers fall back to the run record's status). Delivered as a
   * first-class `run_status` stream event, not through a `useChat`.
   */
  latestRunStatus: AutomationRunStatus | undefined;
  /**
   * Hard kill switch: force-closes the run's live connection (including its
   * auto-reconnect) and settles every demultiplexed thread stream. For when
   * the client knows the stream is over (the run went terminal) and shouldn't
   * wait on the server's sentinel.
   */
  closeConnection: () => void;
}

const DISCONNECTED_TRANSPORT: ChatTransport<UIMessage> = {
  sendMessages: async () => {
    throw new Error("Automation run streams are read-only");
  },
  reconnectToStream: async () => null,
};

// The default (no provider — e.g. the styleguide's static stories) resolves
// every reconnect to null, so the live hooks quietly no-op.
const AutomationRunStreamContext =
  createContext<AutomationRunStreamContextValue>({
    transport: DISCONNECTED_TRANSPORT,
    chatGeneration: 0,
    latestRunStatus: undefined,
    closeConnection: () => undefined,
  });

/** The ambient run-stream transport for the live hooks' `useChat` instances. */
export function useAutomationRunStreamTransport(): ChatTransport<UIMessage> {
  return useContext(AutomationRunStreamContext).transport;
}

/**
 * The `useChat` id for one thread on the run's stream: the thread id scoped by
 * the current chat generation. A generation bump (reconnect replay,
 * `thread_reset`) swaps the id, which restarts `useChat` from an empty message
 * list — the replayed stream re-delivers every chunk, so appending onto the
 * previous chat would duplicate them. The consumer must re-resume on the new id
 * (see `useAutomationRunMessagesLive`); `useChat` only resumes on mount.
 */
export function useAutomationRunStreamChatId(threadId: string): string {
  const { chatGeneration } = useContext(AutomationRunStreamContext);
  return `${chatGeneration}#${threadId}`;
}

/** Inverse of `useAutomationRunStreamChatId`, for the transport side. */
function threadIdFromChatId(chatId: string): string {
  return chatId.slice(chatId.indexOf("#") + 1);
}

/** The run stream's hard kill switch (see `closeConnection`). */
export function useAutomationRunStreamKillSwitch(): () => void {
  return useContext(AutomationRunStreamContext).closeConnection;
}

export interface AutomationRunStatusLive {
  /** The run's latest streamed status, or undefined before any arrives. */
  status: AutomationRunStatus | undefined;
}

/**
 * The run's latest streamed lifecycle status, so the run-detail page reacts to
 * the run starting and finishing without polling the run record. Delivered as
 * a first-class `run_status` event on the run's multiplexed stream (not
 * through a `useChat`). A finished run has no live stream, so the status stays
 * undefined and callers fall back to the run record's status.
 */
export function useAutomationRunStatusLive(): AutomationRunStatusLive {
  return { status: useContext(AutomationRunStreamContext).latestRunStatus };
}

export interface AutomationRunStreamProviderProps {
  automationId: AutomationId;
  runId: AutomationRunId;
  /** False when the run is already terminal on load — there is no live stream. */
  isEnabled: boolean;
  children: ReactNode;
}

/**
 * Owns a run page's single live-stream connection. Mounted once per run-detail
 * page; the status and thread live hooks reach the connection through the
 * ambient transport, so the whole page — status, orchestrator, and every
 * subagent — rides one browser connection. A dropped connection reconnects on
 * its own (bumping the chat generation so consumers restart from the replay);
 * the connection ends for good when the provider unmounts, the kill switch
 * fires, or the run turns terminal (the endpoint answers 204).
 */
export const AutomationRunStreamProvider: FC<
  AutomationRunStreamProviderProps
> = ({ automationId, runId, isEnabled, children }) => {
  const isMountSettled = useIsMountSettled();
  const [chatGeneration, setChatGeneration] = useState(0);
  const [latestRunStatus, setLatestRunStatus] = useState<
    AutomationRunStatus | undefined
  >(undefined);

  // Consumers' `useChat` instances may ask for their thread stream before the
  // connection effect below runs, so they await this deferred instead of
  // racing it.
  const connectionDeferredRef =
    useRef<Deferred<RunStreamConnection | null> | null>(null);
  connectionDeferredRef.current ??= createDeferred();

  useEffect(() => {
    if (!isMountSettled) {
      return;
    }
    if (connectionDeferredRef.current?.isSettled) {
      // The run identity changed under a mounted provider; hand consumers a
      // fresh deferred (their chat ids change with the run, so they reconnect).
      connectionDeferredRef.current = createDeferred();
    }
    const deferred = connectionDeferredRef.current;
    if (!deferred) {
      return;
    }
    // A fresh connection replays its own run's status from scratch.
    setLatestRunStatus(undefined);
    if (!isEnabled) {
      deferred.resolve(null);
      return;
    }
    const connection = createRunStreamConnection(
      `${apiBaseUrl}/automations/${encodeURIComponent(automationId)}/runs/${encodeURIComponent(runId)}/stream`,
      () => setChatGeneration((generation) => generation + 1),
      setLatestRunStatus,
    );
    deferred.resolve(connection);
    return () => connection.abort();
  }, [isMountSettled, isEnabled, automationId, runId]);

  const contextValue = useMemo<AutomationRunStreamContextValue>(
    () => ({
      transport: {
        sendMessages: async () => {
          throw new Error("Automation run streams are read-only");
        },
        reconnectToStream: async ({ chatId }) => {
          const connection = await connectionDeferredRef.current?.promise;
          return connection
            ? connection.connectToThread(threadIdFromChatId(chatId))
            : null;
        },
      },
      chatGeneration,
      latestRunStatus,
      // Aborting as soon as the connection exists covers a kill that fires
      // while the connect effect (or the fetch) is still in flight.
      closeConnection: () => {
        void connectionDeferredRef.current?.promise.then((connection) =>
          connection?.abort(),
        );
      },
    }),
    [chatGeneration, latestRunStatus],
  );

  return (
    <AutomationRunStreamContext.Provider value={contextValue}>
      {children}
    </AutomationRunStreamContext.Provider>
  );
};
